import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { fetchMembers } from '@/features/members/api';
import {
  createNgRule,
  fetchNgRules,
  NG_RULE_STATUS_LABELS,
  setNgRuleStatus,
  type NgRule,
} from '@/features/seating/api';
import { ApiError } from '@/lib/api/http';

/**
 * 業務マスター > 席次NG（implementation-spec §9）。
 * 事務局は即時有効で登録でき、サクセスからの申請（requested）は承認・却下できる。
 * confidential_reason は事務局にのみ表示する（このページ自体が事務局専用）。
 */
export function NgRulesPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['ng-rules'], queryFn: fetchNgRules });
  const membersQuery = useQuery({
    queryKey: ['members-picker'],
    queryFn: () => fetchMembers({ per_page: 100 }),
  });

  const [memberAId, setMemberAId] = useState('');
  const [memberBId, setMemberBId] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [confidentialReason, setConfidentialReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createNgRule({
        member_a_id: memberAId,
        member_b_id: memberBId,
        reason_category: reasonCategory,
        confidential_reason: confidentialReason || null,
      });
      setMemberAId('');
      setMemberBId('');
      setReasonCategory('');
      setConfidentialReason('');
      await queryClient.invalidateQueries({ queryKey: ['ng-rules'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSetStatus(rule: NgRule, status: NgRule['status']): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setNgRuleStatus(rule.id, status);
      await queryClient.invalidateQueries({ queryKey: ['ng-rules'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<NgRule>[] = [
    {
      key: 'members',
      header: '対象',
      primary: true,
      render: (r) => `${r.member_a_name ?? r.member_a_id} × ${r.member_b_name ?? r.member_b_id}`,
    },
    { key: 'reason_category', header: '区分', render: (r) => r.reason_category },
    { key: 'confidential_reason', header: '理由（機密）', render: (r) => r.confidential_reason ?? '—' },
    { key: 'source', header: '起票', render: (r) => (r.source === 'office' ? '事務局' : 'サクセス申請') },
    { key: 'status', header: '状態', render: (r) => NG_RULE_STATUS_LABELS[r.status] },
  ];

  return (
    <>
      <PageHeader
        title="席次NG"
        description="同じ卓にしない会員の組み合わせを登録します。ここで承認したものが、イベント詳細の「席次」タブで警告として効きます（警告が出ても理由を書けば確定はできます）。サクセス担当からの申請もここで承認します。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">会員A</span>
            <select
              required
              value={memberAId}
              onChange={(e) => setMemberAId(e.target.value)}
              className="min-w-[14rem] rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">選択してください</option>
              {(membersQuery.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}（{m.member_id}）
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">会員B</span>
            <select
              required
              value={memberBId}
              onChange={(e) => setMemberBId(e.target.value)}
              className="min-w-[14rem] rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">選択してください</option>
              {(membersQuery.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}（{m.member_id}）
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">区分</span>
            <input
              required
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              placeholder="例: 取引関係"
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">理由（機密・事務局のみ閲覧）</span>
            <input
              value={confidentialReason}
              onChange={(e) => setConfidentialReason(e.target.value)}
              className="min-w-[16rem] rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={busy || !memberAId || !memberBId || !reasonCategory}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            登録する
          </button>
        </form>
      </div>

      <ListTable<NgRule>
        caption="席次NGルール一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        state={state}
        onRetry={() => void query.refetch()}
        rowActions={[
          {
            label: '承認（有効化）',
            onSelect: (r) => void onSetStatus(r, 'active'),
          },
          {
            label: '却下',
            onSelect: (r) => void onSetStatus(r, 'rejected'),
            destructive: true,
          },
          {
            label: '無効化',
            onSelect: (r) => void onSetStatus(r, 'archived'),
            destructive: true,
          },
        ]}
        emptyTitle="NGルールは登録されていません"
      />
    </>
  );
}
